import React from 'react';

const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '$0';
  return `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const RentToOwnStockSummary = ({ investorResult, borrowerResult, audience }) => {
  if (!investorResult) {
    return <div className="text-center text-gray-500">No data available for Rent-to-Own + Stock.</div>;
  }

  return (
    <div className="bg-white shadow-md rounded-lg p-0 w-full">
      <h2 className="text-2xl font-bold text-center text-blue-700 mb-6">Rent-to-Own + Stock Summary</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 px-2">
        {/* Investor Summary */}
        <div className="bg-gray-50 rounded-md p-4 md:p-5">
          <h3 className="text-blue-800 text-xl font-semibold text-center mb-4">Investor Summary</h3>
          <div className="grid gap-y-2 text-sm" style={{ display: 'grid', gridTemplateColumns: '1fr auto' }}>
            <div>Capital Invested:</div>
            <div className="font-semibold">{formatCurrency(investorResult.originalPropertyValue)}</div>

            <div>Total Rent Collected:</div>
            <div className="font-semibold">{formatCurrency(investorResult.totalRentCollected)}</div>

            <div>Equity Paid Over Time:</div>
              <div className="font-semibold">{formatCurrency(borrowerResult.totalBuyout)}</div>

              <div>Total Expenses (Tax + Insurance + Maintenance):</div>
            <div className="font-semibold">{formatCurrency(investorResult.totalExpenses)}</div>


            <div>Net Cashflow (Rent − Expenses):</div>
            <div className="font-semibold">{formatCurrency(investorResult.netCashflow)}</div>

           
            <div>Final Asset Value:</div>
            <div className="font-semibold">{formatCurrency(investorResult.finalValue)}</div>

            <div>Buyout & Gains (Cash):</div>
            <div className="font-semibold">{formatCurrency(investorResult.nonReinvestedCash)}</div>

            <div>Reinvested Capital:</div>
            <div className="font-semibold">{formatCurrency(investorResult.reinvestedCapital)}</div>

            <div>Stock Accumulated with Monthly Equity Payouts:</div>
            <div className="font-semibold">{investorResult.totalStockShares?.toFixed(2)}</div>

            <div>Stock Value at Exit:</div>
            <div className="font-semibold">{formatCurrency(investorResult.stockValueAtExit)}</div>

            <div>Exit Value (Cashflow + Equity + Appreciation − Sell Cost):</div>
            <div className="font-semibold">{formatCurrency(investorResult.exitValue)}</div>

            <div>Cash-on-Cash Return:</div>
            <div className="font-semibold">{investorResult.cashOnCashReturn}%</div>

            <div>Total ROI:</div>
            <div className="font-semibold">{investorResult.totalROI}%</div>

            <div>Annualized ROI:</div>
            <div className="font-semibold">{investorResult.annualizedROI || 0}%</div>

            <div>IRR Estimate:</div>
            <div className="font-semibold">{investorResult.irrEstimate || 0}%</div>

            <div>Annual NOI:</div>
            <div className="font-semibold">{formatCurrency(investorResult.annualNOI)}</div>

            <div>Cap Rate:</div>
            <div className="font-semibold">{investorResult.capRate}%</div>

            <div>Investment Term:</div>
            <div className="font-semibold">{investorResult.termYears} years</div>
          </div>
          <p className="text-xs text-gray-600 mt-3 text-center leading-snug">
            * Exit value includes rent income, equity payback, and appreciation. Expenses reduce net cashflow. Values are after closing cost deduction.
          </p>
        </div>

        {/* Entrepreneur Summary */}
        <div className="bg-purple-50 rounded-md p-4 md:p-5 flex flex-col justify-between">
          <div>
            <h3 className="text-purple-700 text-xl font-semibold text-center mb-4">Entrepreneur Summary</h3>
            <div className="grid gap-y-2 text-sm" style={{ display: 'grid', gridTemplateColumns: '1fr auto' }}>
              <div>Average Monthly Payment (Rent only):</div>
              <div className="font-semibold">{formatCurrency(borrowerResult.monthlyRentPayment)}</div>

              <div>Average Monthly Payment (Rent + Equity):</div>
              <div className="font-semibold">{formatCurrency(borrowerResult.monthlyPayment)}</div>

              <div>Rent Paid Over Time:</div>
              <div className="font-semibold">{formatCurrency(borrowerResult.totalRentPaid)}</div>

              <div>Equity Paid Over Time:</div>
              <div className="font-semibold">{formatCurrency(borrowerResult.totalBuyout)}</div>

              <div>Total Expenses (Tax + Insurance + Maintenance):</div>
              <div className="font-semibold">{formatCurrency(borrowerResult.totalExpenses)}</div>

              <div>Total Paid (Rent + Equity + Expenses):</div>
              <div className="font-semibold">{formatCurrency(borrowerResult.totalPayment)}</div>

              <div>Total Equity:</div>
              <div className="font-semibold">{formatCurrency(borrowerResult.equityBuilt)}</div>

              <div>Exit Value (Asset Value × Ownership − Sell Cost):</div>
              <div className="font-semibold">{formatCurrency(borrowerResult.exitValue)}</div>

              <div>Investment Term:</div>
              <div className="font-semibold">{borrowerResult.termYears} years</div>
            </div>
          </div>
          <p className="text-xs text-gray-600 mt-3 text-center leading-snug">
            * Total paid includes all rent and equity contributions. Equity builds monthly and determines final ownership-based asset share.
          </p>
        </div>

      </div>
    </div>
  );
};

export default RentToOwnStockSummary;
